/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
} from '@zapier/universal-layout';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import {
  BREAKPOINTS,
  MEDIA_MAX_WIDTH,
  MEDIA_MIN_WIDTH,
} from '../../../constants';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../types';

export type StackingCardsSectionProps = {
  heading?: string;
  cards?: {
    label?: string;
    heading?: string;
    details?: RichTextHTMLString;
    image?: {
      url?: string;
      alt?: string;
    };
  }[];
};

const disabledStickyMedia =
  `(max-width: ${BREAKPOINTS.small}px) and (max-height: 832px), ` +
  `(min-width: ${BREAKPOINTS.small}px) and (max-height: 736px), ` +
  `(min-width: ${BREAKPOINTS.large}px) and (max-height: 864px)`;
const disabledStickyMediaQuery = `@media ${disabledStickyMedia}`;

const Styles = labelStyles('StackingCardsSection', {
  root: css({
    '--card-height': '544px',
    padding: '32px 20px',

    [MEDIA_MIN_WIDTH.small]: {
      '--card-height': '480px',
      padding: '96px 20px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      '--card-height': '576px',
      padding: '96px 20px',
    },
  }),

  heading: css({
    maxWidth: '224px',
    margin: '0 auto calc(var(--card-height) + 32px)',
    position: 'sticky',
    top: `${UNIVERSAL_TOPBAR_CONDENSED_HEIGHT + 48}px`,
    fontFamily: Typography.heading.fontFamily,
    fontWeight: 600,
    fontSize: '40px',
    textAlign: 'center',
    lineHeight: 1,
    color: Colors.earth,

    [`@media (max-width: ${BREAKPOINTS.small}px) and (max-height: 864px)`]: {
      marginBottom: 'calc(var(--card-height) + 16px)',
      top: `${UNIVERSAL_TOPBAR_CONDENSED_HEIGHT + 32}px`,
    },

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '768px',
      marginBottom: 'calc(var(--card-height) + 48px)',
      top: `${UNIVERSAL_TOPBAR_HEIGHT + 64}px`,

      '@media (max-height: 800px)': {
        top: `${UNIVERSAL_TOPBAR_HEIGHT + 32}px`,
      },
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '70px',
      marginBottom: 'calc(var(--card-height) + 96px)',
      top: `${UNIVERSAL_TOPBAR_HEIGHT + 64}px`,

      '@media (max-height: 960px)': {
        marginBottom: 'calc(var(--card-height) + 64px)',
        top: `${UNIVERSAL_TOPBAR_HEIGHT + 32}px`,
      },
    },

    [disabledStickyMediaQuery]: {
      position: 'static',
      marginBottom: '32px',

      [MEDIA_MIN_WIDTH.small]: {
        marginBottom: '48px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        marginBottom: '96px',
      },
    },
  }),

  cards: css({
    marginTop: 'calc(var(--card-height) * -1)',
    display: 'grid',
    gap: '32px',

    [MEDIA_MIN_WIDTH.small]: {
      gap: '48px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      gap: '96px',
    },

    [disabledStickyMediaQuery]: {
      marginTop: '0',
    },
  }),

  card: css({
    width: '100%',
    maxWidth: '960px',
    height: 'var(--card-height)',
    margin: '0 auto',
    padding: '40px',
    boxSizing: 'border-box',
    display: 'grid',
    placeItems: 'center',
    gap: '20px',
    position: 'sticky',
    top: `${UNIVERSAL_TOPBAR_CONDENSED_HEIGHT + 160}px`,
    backgroundColor: Colors.neutral100,
    border: `1px solid ${Colors.earth}`,
    borderRadius: '20px',
    transform: 'translateY(var(--card-offset))',
    transformOrigin: 'center top',
    transition: 'box-shadow 500ms ease-in-out',

    '&[data-active="true"]': {
      boxShadow: `4px 4px 0px ${Colors.earth}`,
    },

    [`@media (max-width: ${BREAKPOINTS.small}px) and (max-height: 864px)`]: {
      top: `${UNIVERSAL_TOPBAR_CONDENSED_HEIGHT + 128}px`,
    },

    [MEDIA_MIN_WIDTH.small]: {
      gridTemplateColumns: '1fr 1fr',
      gap: '56px',
      alignItems: 'center',
      top: `${UNIVERSAL_TOPBAR_HEIGHT + 152}px`,
      padding: '72px 48px',

      '@media (max-height: 800px)': {
        top: `${UNIVERSAL_TOPBAR_HEIGHT + 120}px`,
      },
    },

    [MEDIA_MIN_WIDTH.large]: {
      gap: '64px',
      top: `${UNIVERSAL_TOPBAR_HEIGHT + 230}px`,
      padding: '72px 80px 72px 96px',

      '@media (max-height: 960px)': {
        top: `${UNIVERSAL_TOPBAR_HEIGHT + 166}px`,
      },
    },

    [disabledStickyMediaQuery]: {
      position: 'static',
      transform: 'none !important',

      '&[data-active="true"]': {
        boxShadow: 'none',
      },
    },
  }),

  cardImage: css({
    maxWidth: 'min(320px, 100%)',
    maxHeight: '232px',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '100%',
      maxHeight: 'calc(var(--card-height) - 144px)',
    },
  }),

  cardContent: css({
    maxWidth: '384px',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: 'none',
    },
  }),

  cardLabel: css({
    fontSize: '16px',
    lineHeight: 1.25,
    color: Colors.earth,

    [MEDIA_MAX_WIDTH.large]: {
      display: 'none',
    },
  }),

  cardHeading: css({
    fontFamily: Typography.heading.fontFamily,
    fontWeight: 600,
    fontSize: '26px',
    lineHeight: 1.1,
    color: Colors.earth,
    marginBottom: '12px',

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '32px',
      marginBottom: '16px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '40px',
      marginBottom: '20px',
    },
  }),

  cardDetails: css({
    fontSize: '16px',
    lineHeight: 1.5,
    color: Colors.earth,

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '18px',
    },
  }),
});

export function StackingCardsSection(props: StackingCardsSectionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);

  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [sticky, setSticky] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setSticky(!window.matchMedia(disabledStickyMedia).matches);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const root = rootRef.current;

    if (!root || !sticky) {
      return;
    }

    const handleScroll = () => {
      if (!cardsRef.current) {
        return;
      }

      const rootTop = root.getBoundingClientRect().top;
      let _activeCardIndex = 0;

      cardsRef.current.forEach((card, index) => {
        const curr = card.getBoundingClientRect();
        const prev = cardsRef.current[index - 1]?.getBoundingClientRect();

        const insetTop = parseInt(
          window
            .getComputedStyle(card)
            .getPropertyValue('top')
            .replace('px', ''),
        );

        const offset = insetTop - rootTop - index * curr.height;
        if (offset > 0) {
          card.style.transform = `translateY(var(--card-offset)) scale(${
            (curr.height - offset * 0.05) / curr.height
          })`;
        }

        if (prev && curr.top < prev.bottom - 0.2 * prev.height) {
          _activeCardIndex = index;
        }
      });

      setActiveCardIndex(_activeCardIndex);
    };

    const observer = new IntersectionObserver(([{ isIntersecting }]) => {
      if (isIntersecting) {
        window.addEventListener('scroll', handleScroll);
      } else {
        window.removeEventListener('scroll', handleScroll);
      }
    });

    observer.observe(root);

    return () => {
      observer.unobserve(root);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sticky]);

  return (
    <div ref={rootRef} css={Styles.root}>
      <h2 css={Styles.heading}>{props.heading}</h2>
      <div css={Styles.cards}>
        {props.cards?.map((card, index) => (
          <div
            key={index}
            ref={(node) => {
              if (node) {
                cardsRef.current[index] = node;
              }
            }}
            css={Styles.card}
            style={
              { '--card-offset': `${index * 20}px` } as CSSProperties & {
                '--card-offset'?: string;
              }
            }
            data-active={activeCardIndex === index}
          >
            {card.image?.url && (
              <img
                css={Styles.cardImage}
                src={card.image.url}
                alt={card.image.alt ?? ''}
              />
            )}
            <div css={Styles.cardContent}>
              <div css={Styles.cardLabel}>{card.label}</div>
              <h3 css={Styles.cardHeading}>{card.heading}</h3>
              {card.details && (
                <div css={Styles.cardDetails}>
                  <RichTextHtml html={card.details} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
